import type { ScatterPlotPoint } from '../../charts/scatter-plot/scatter-plot-chart.model';
import { computeRegression, type RegressionResult } from '../../charts/scatter-plot/scatter-plot-chart.util';
import type { CorrelationMeasureId } from './correlation-measures.model';

// =============================================================================
// Types
// =============================================================================

export type CorrelationMeasureValues = ReadonlyMap<CorrelationMeasureId, number | string>;

/** Intermediate sums needed by the formula builder to show plugged-in computations. */
export interface CorrelationIntermediates {
  readonly n: number;
  readonly sumX: number;
  readonly sumY: number;
  readonly sumXY: number;
  readonly sumX2: number;
  readonly sumY2: number;
  readonly meanX: number;
  readonly meanY: number;
  readonly stdX: number;
  readonly stdY: number;
  readonly xValues: readonly number[];
  readonly yValues: readonly number[];
  readonly regression: RegressionResult | null;
}

// =============================================================================
// Computation
// =============================================================================

/** Compute all bivariate measures from paired data points. */
export function computeAllCorrelationMeasures(points: readonly ScatterPlotPoint[]): CorrelationMeasureValues {
  const n = points.length;
  if (n < 2) {
    return new Map();
  }

  const inter = computeCorrelationIntermediates(points);
  const result = new Map<CorrelationMeasureId, number | string>();

  result.set('n', n);
  result.set('meanX', roundTo(inter.meanX, 6));
  result.set('meanY', roundTo(inter.meanY, 6));
  result.set('stdX', roundTo(inter.stdX, 6));
  result.set('stdY', roundTo(inter.stdY, 6));

  if (inter.regression) {
    result.set('r', inter.regression.r);
    result.set('rSquared', inter.regression.rSquared);
    result.set('slope', inter.regression.a);
    result.set('intercept', inter.regression.b);
    result.set('equation', formatEquation(inter.regression.a, inter.regression.b));
  }

  return result;
}

/** Compute intermediate sums and statistics needed for formula display. */
export function computeCorrelationIntermediates(points: readonly ScatterPlotPoint[]): CorrelationIntermediates {
  const n = points.length;
  const xValues = points.map((p) => p.x);
  const yValues = points.map((p) => p.y);

  let sumX = 0;
  let sumY = 0;
  let sumXY = 0;
  let sumX2 = 0;
  let sumY2 = 0;

  for (const p of points) {
    sumX += p.x;
    sumY += p.y;
    sumXY += p.x * p.y;
    sumX2 += p.x * p.x;
    sumY2 += p.y * p.y;
  }

  const meanX = sumX / n;
  const meanY = sumY / n;

  let varX = 0;
  let varY = 0;
  for (const p of points) {
    varX += (p.x - meanX) ** 2;
    varY += (p.y - meanY) ** 2;
  }
  const stdX = Math.sqrt(varX / n);
  const stdY = Math.sqrt(varY / n);

  const regression = computeRegression(points as ScatterPlotPoint[]);

  return { n, sumX, sumY, sumXY, sumX2, sumY2, meanX, meanY, stdX, stdY, xValues, yValues, regression };
}

// =============================================================================
// Formatting
// =============================================================================

/** Format a correlation measure value for display. */
export function formatCorrelationValue(id: CorrelationMeasureId, value: number | string | undefined): string {
  if (value === undefined || value === null) {
    return '—';
  }

  if (typeof value === 'string') {
    return value;
  }

  switch (id) {
    case 'n':
      return String(value);
    case 'r':
    case 'rSquared': {
      const sign = value >= 0 ? '+' : '';
      return id === 'r' ? `${sign}${value.toFixed(4)}` : value.toFixed(4);
    }
    default:
      return formatNumber(value);
  }
}

// =============================================================================
// Helpers
// =============================================================================

function formatEquation(a: number, b: number): string {
  const aStr = formatNumber(a);
  if (b >= 0) {
    return `ŷ = ${aStr}x + ${formatNumber(b)}`;
  }
  return `ŷ = ${aStr}x − ${formatNumber(Math.abs(b))}`;
}

function formatNumber(v: number): string {
  if (Number.isInteger(v)) {
    return String(v);
  }
  const s = parseFloat(v.toFixed(4)).toString();
  return s;
}

function roundTo(value: number, decimals: number): number {
  const factor = Math.pow(10, decimals);
  return Math.round(value * factor) / factor;
}
